/**
 * @file: panelContext.tsx
 *
 * Lets the chat screens reused inside the floating panel's standalone stack ask
 * the panel to close itself. The room-detail screen's terminal actions
 * (leave / dismiss / delete-thread) normally `popToTop()`, but in the floating
 * stack the root IS the conversation — which the action just destroyed — so
 * there's nowhere good to pop to; closing the panel is correct. Undefined
 * outside the panel (the inbox), where `popToTop()` to the inbox list is right.
 */

import { ReactNode, createContext, memo, useContext, useMemo } from 'react';

const FloatingPanelContext = createContext<{ close: () => void } | null>(null);

export const FloatingPanelProvider = memo(
  ({ close, children }: { close: () => void; children: ReactNode }) => {
    const value = useMemo(() => ({ close }), [close]);
    return (
      <FloatingPanelContext.Provider value={value}>
        {children}
      </FloatingPanelContext.Provider>
    );
  },
);

/** The panel's close fn when rendered inside the floating panel; else undefined. */
export const useFloatingPanelClose = (): (() => void) | undefined =>
  useContext(FloatingPanelContext)?.close;
